import { Account as AccountV2 } from '@lib/model/account-model';
import { Group as GroupV2 } from '@lib/model/group-model';
import { Actor } from '@lib/permissions/actor';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelectInternetDeviceById } from '@lib/react/tanstack-query/internet-device-queries';
import { useTranslation } from 'react-i18next';

export type ActorData = {
  actor: Actor;
  account?: AccountV2;
  group?: GroupV2;
};

export function useActorData() {
  const [searchParams] = useSearchParams();
  const impersonateAccountId = searchParams.get('impersonateAccountId');
  const { i18n } = useTranslation();
  return useQuery<ActorData>({
    queryKey: ['actor-data', impersonateAccountId],
    queryFn: async () => {
      let url = '/api/v2/actor';
      if (!!impersonateAccountId) {
        url += `?impersonateAccountId=${impersonateAccountId}`;
      }
      const response = await fetch(url);
      if (!response.ok) {
        return Promise.reject(response.statusText);
      }
      const actor = (await response.json()) as ActorData;
      const language =
        actor.actor?.actorLanguage ??
        actor.group?.defaultLanguage ??
        actor.account?.defaultLanguage;
      if (language !== '' && language !== i18n.language) {
        i18n.changeLanguage(language);
      }
      return actor;
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
    retryOnMount: false,
  });
}

type TenantData = {
  name: string;
  slug?: string;
  connectionTestUrl: string;
  domain: string;
  ipSyncUrl: string;
};

export function useTenantData() {
  return useQuery<TenantData>({
    queryKey: ['tenant-data'],
    queryFn: () =>
      fetch('/api/v2/tenant').then((response) => response.json() as Promise<TenantData>),
    staleTime: Infinity,
    retry: false,
  });
}

export function useMyDevice() {
  const actorData = useActorData();
  const actor = actorData.data?.actor;
  const internetDeviceQuery = useSelectInternetDeviceById(
    {
      queryKey: ['internet-device', 'mine'],
      id: actor?.actorId || '',
    },
    {
      enabled: actor?.actorType === 'InternetDevice',
    },
  );
  return useMemo(
    () => ({
      account: internetDeviceQuery.data?.account,
      group: internetDeviceQuery.data?.group,
      internetDevice: internetDeviceQuery.data?.internetDevice,
      isLoading: internetDeviceQuery.isLoading,
      isError: !!internetDeviceQuery.error,
    }),
    [
      internetDeviceQuery.data?.account,
      internetDeviceQuery.data?.group,
      internetDeviceQuery.data?.internetDevice,
      internetDeviceQuery.error,
      internetDeviceQuery.isLoading,
    ],
  );
}
